/**
 * KB Suggester — fetches and renders KB suggestions for a ticket or
 * prospective ticket (NewTicketDialog).
 *
 * Two modes:
 *   ticketId — for TicketDetail right rail
 *   draft    — pass {main_category, sub_category, issue_type, q} for NewTicket
 *
 * Auto-expands when ≥1 result returned; collapses with "💡 N suggestions" pill
 * when 0 results.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, BookOpen, ChevronDown, ChevronUp, Eye } from "lucide-react";
import KbArticleDrawer from "@/components/kb/KbArticleDrawer";

export default function KbSuggesterPanel({
  ticketId,
  draft,
  onInsertLink,
  variant = "panel",   // "panel" (TicketDetail) | "inline" (NewTicketDialog)
  limit = 5,
  debounceMs = 350,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [drawerArticle, setDrawerArticle] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    const params = { limit };
    if (ticketId) {
      params.ticket_id = ticketId;
    } else if (draft) {
      if (draft.main_category) params.main_category = draft.main_category;
      if (draft.sub_category) params.sub_category = draft.sub_category;
      if (draft.issue_type) params.issue_type = draft.issue_type;
      if (draft.q) params.q = draft.q;
    }
    // No signal at all = empty result, save a roundtrip
    const hasSignal = params.ticket_id || params.main_category || params.sub_category || params.issue_type || params.q;
    if (!hasSignal) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await api.get("/kb/suggest", { params });
      setRows(r.data?.rows || []);
      setExpanded((r.data?.rows || []).length > 0);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [ticketId, draft, limit]);

  // Debounce — refetch on dep changes
  const depsKey = useMemo(() => JSON.stringify({ ticketId, draft }), [ticketId, draft]);
  useEffect(() => {
    const t = setTimeout(fetchSuggestions, debounceMs);
    return () => clearTimeout(t);
  }, [depsKey, fetchSuggestions, debounceMs]);

  const onPick = (row) => {
    setDrawerArticle(row);
    setDrawerOpen(true);
  };

  // Compact inline strip for NewTicketDialog
  if (variant === "inline") {
    if (!rows.length && !loading) return null;
    return (
      <>
        <div className="rounded-md bg-sky-50/60 border border-sky-200 px-3 py-2"
             data-testid="kb-inline-suggester">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-sky-600" />
            <span className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">
              Related KB articles ({rows.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {rows.slice(0, 3).map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onPick(r)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-white border border-sky-200 text-sky-800 hover:bg-sky-100 hover:border-sky-300 transition"
                data-testid={`kb-inline-suggestion-${r.id}`}
              >
                <BookOpen className="w-3 h-3" />
                <span className="max-w-[260px] truncate">{r.title}</span>
              </button>
            ))}
          </div>
        </div>
        <KbArticleDrawer
          articleId={drawerArticle?.id}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          ticketId={ticketId || null}
        />
      </>
    );
  }

  // Full panel for TicketDetail right rail
  return (
    <>
      <Card className="border-slate-200" data-testid="kb-suggester-panel">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded((v) => !v)}>
          <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
              KB Suggestions
              {rows.length > 0 && (
                <span className="text-[10px] text-indigo-600 font-bold normal-case tracking-normal">
                  {rows.length}
                </span>
              )}
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </CardTitle>
        </CardHeader>
        {expanded && (
          <CardContent className="-mt-1 space-y-1.5">
            {loading && rows.length === 0 && (
              <div className="text-xs text-slate-400 py-2">Searching for relevant articles…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="text-xs text-slate-400 italic py-2">No related articles found.</div>
            )}
            {rows.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onPick(r)}
                className="w-full text-left rounded-md border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 transition px-2.5 py-2"
                data-testid={`kb-suggestion-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-800 truncate flex items-center gap-1">
                      <span className="text-[10px] text-indigo-500 font-mono">#{i + 1}</span> {r.title}
                    </div>
                    {r.summary && (
                      <div className="text-[10px] text-slate-500 line-clamp-2 mt-0.5">{r.summary}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {r.category && (
                        <Badge variant="outline" className="text-[9px] bg-slate-50 border-slate-200 text-slate-600">{r.category}</Badge>
                      )}
                      {(r.tags || []).slice(0, 2).map((t) => (
                        <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[9px]">{t}</Badge>
                      ))}
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5 shrink-0">
                    <Eye className="w-3 h-3" />{r.view_count || 0}
                  </span>
                </div>
              </button>
            ))}
            {onInsertLink && rows.length > 0 && (
              <div className="text-[10px] text-slate-400 italic pt-1">
                Tip: click an article to preview, then "Insert link" copies it into your next reply.
              </div>
            )}
            <div className="pt-1">
              <Button variant="ghost" size="sm" className="text-[11px] text-indigo-600 hover:text-indigo-700 h-6 px-1"
                      onClick={() => window.open("/knowledge-base", "_blank")}
                      data-testid="kb-suggester-browse-btn">
                Browse all KB →
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
      <KbArticleDrawer
        articleId={drawerArticle?.id}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        ticketId={ticketId || null}
        onInsertLink={onInsertLink}
      />
    </>
  );
}
